<script lang="ts">
    import { flip } from 'svelte/animate';
    import { makeNotFavourite, makeFavourite, topics } from '../store/topics';
    import { onMount } from 'svelte';
    import { getTopics } from '../actions/topics';
    import PrivateLink from './guard/PrivateLink.svelte';
    import InfiniteScroll from 'svelte-infinite-scroll';
    import ScreenreaderOnly from './ScreenreaderOnly.svelte';
    import FavouriteStar from '../assets/heart_filled.svg';
    import NotFavouriteStar from '../assets/heart.svg';
    import TrashCan from '../assets/trash-can.svg';
    import Hide from '../assets/hide.svg';
    import { quintOut } from 'svelte/easing';
    import { crossfade } from 'svelte/transition';
    import { loginStatus } from '../store/loginV2';
    import { deletePresentation } from '../actions/deletePresentation';
    import { unpublishPresentation } from '../actions/unpublishPresentation';
    import { LoginStatus } from '../../common/types';

    const numberOfTopicsToLoad = 20;
    let numberOfTopics = numberOfTopicsToLoad;
    let scrollContainer;

    const [send, receive] = crossfade({
        duration: (d) => Math.sqrt(d * 200),

        fallback(node) {
            const style = getComputedStyle(node);
            const transform = style.transform === 'none' ? '' : style.transform;

            return {
                duration: 600,
                easing: quintOut,
                css: (t) => `
					transform: ${transform} scale(${t});
					opacity: ${t}
				`,
            };
        },
    });

    const loadMore = () => {
        console.log('Loading more presentations');
        numberOfTopics += numberOfTopicsToLoad;
    };

    onMount(() => {
        if (!$topics || $topics.length === 0) {
            getTopics();
        }
    });

    function getBackground(imageUrl?: string) {
        return imageUrl
            ? `background-image: url("${imageUrl}");`
            : 'background-color: #2ab7ca';
    }

    function getFavouriteStyle(isFavourite?: boolean) {
        return `background-image: url("${
            isFavourite ? FavouriteStar : NotFavouriteStar
        }");`;
    }

    function getFavouriteAction(id: string, isFavourite?: boolean) {
        return isFavourite ? () => makeNotFavourite(id) : makeFavourite(id);
    }

    function getDeleteAction(id: string) {
        return () =>
            deletePresentation(id).then(
                () => {},
                (err) => {
                    console.error(err);
                    alert('Failed to delete Presentation');
                }
            );
    }

    function getUnpublishAction(id: string) {
        return () =>
            unpublishPresentation(id).then(
                () => {},
                (err) => {
                    console.error(err);
                    alert('Failed to unpublish Presentation');
                }
            );
    }

    const deleteButtonStyle = `background-image: url("${TrashCan}");`;
    const unpublishButtonStyle = `background-image: url("${Hide}");`;
</script>

<div class="presentation-container">
    <ul class="presentation-list" bind:this={scrollContainer}>
        {#if $loginStatus.status === LoginStatus.LOGGED_IN}
            <li class="presentation-item new-presentation">
                <PrivateLink to="/new-presentation"
                    ><span>+</span>
                    <ScreenreaderOnly>new presentation</ScreenreaderOnly>
                </PrivateLink>
            </li>
        {:else}
            <li class="presentation-item new-presentation-placeholder">
                <span>Log in<br />to<br />create AI slides!</span>
            </li>
        {/if}

        {#each $topics.slice(0, numberOfTopics) as topic (topic.id)}
            <li
                in:receive={{ key: topic.id }}
                animate:flip={{ duration: 300 }}
                class="presentation-item"
            >
                <a
                    class="presentation-link zoom"
                    href={topic.url}
                    style={getBackground(topic.previewImage)}
                />
                <a href={topic.url} class="presentation-title">{topic.title}</a>
                <div class="button-container">
                    <button
                        class="button"
                        aria-label="favourite"
                        on:click={getFavouriteAction(topic.id, topic.favourite)}
                        style={getFavouriteStyle(topic.favourite)}
                    />
                    {#if $loginStatus.status === LoginStatus.LOGGED_IN}
                        <button
                            class="button"
                            aria-label="delete"
                            on:click={getDeleteAction(topic.id)}
                            style={deleteButtonStyle}
                        />
                        <button
                            class="button"
                            aria-label="unpublish"
                            on:click={getUnpublishAction(topic.id)}
                            style={unpublishButtonStyle}
                        />
                    {/if}
                </div>
            </li>
        {/each}
    </ul>
    <InfiniteScroll
        hasMore={numberOfTopics < $topics.length}
        threshold={0}
        on:loadMore={loadMore}
    />
</div>

<style>
    .zoom {
        transition: transform 0.3s; /* Animation */
    }

    .zoom:hover {
        transform: scale(
            1.1
        ); /* (150% zoom - Note: if the zoom is too large, it will go outside of the viewport) */
    }

    .presentation-container {
        height: 100%;
        overflow-y: auto;
        overflow-x: hidden;
    }

    .presentation-list {
        width: 90%;
        margin-left: 5%;
        display: grid;
        list-style: none;
        padding: 1rem;
        grid-template-columns: repeat(auto-fit, minmax(24.5rem, 1fr));
        grid-auto-rows: 21rem;
        gap: 1rem;
        place-items: center;
    }

    .presentation-item {
        box-shadow: 0.3rem 0.3rem 1rem 0 rgba(0, 0, 0, 0.2);
        overflow: hidden;
        position: relative;
        width: 25rem;
        height: 19rem;
        border-radius: 1rem;
    }

    .presentation-item:hover {
        box-shadow: 0 0 1.5rem 0 rgba(21, 118, 238, 0.7);
    }

    .button {
        all: unset;
        position: absolute;
        height: 2rem;
        width: 2rem;
        border: none;
        background-color: transparent;
        background-size: contain;
        background-repeat: no-repeat;
        cursor: pointer;

        position: relative;
        background-position: center right;
        margin: 0.2rem;
    }

    .button:hover {
        background-color: rgba(255, 255, 255, 0.8);
        outline: 2px solid rgba(255, 255, 255, 0.8);
        border-radius: 0.5rem;
    }

    .button-container {
        width: 100%;
        height: 2rem;
        position: absolute;
        top: 0;
        padding: 1rem;
        background-position: center right;
    }

    .presentation-link {
        display: block;
        height: 100%;
        background-repeat: no-repeat;
        background-size: cover;
        box-sizing: border-box;
        text-align: center;
    }

    .presentation-link:link,
    .presentation-link:visited {
        color: var(--primary-foreground);
        text-decoration: none;
    }

    .presentation-title {
        color: #444444;
        display: block;
        position: absolute;
        width: 100%;
        bottom: 0;
        background-color: rgba(255, 255, 255, 0.95);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        text-align: center;
        font-size: 1.5rem;
        font-weight: bold;
        padding: 1rem;
        box-sizing: border-box;
        text-decoration: none;
    }

    .new-presentation-placeholder {
        color: #a3a3a3;
        font-weight: bold;
        font-size: 2rem;
        box-sizing: border-box;
        border: 0.4rem dashed #a3a3a3;
        border-radius: 1rem;
        text-align: center;
        display: flex;
        justify-content: center;
        align-items: center;
    }

    .new-presentation-placeholder:hover {
        box-shadow: 0.3rem 0.3rem 1.5rem 0 rgba(0, 0, 0, 0.2);
        color: #666666;
        border: 0.4rem dashed #666666;
    }

    .new-presentation {
        font-size: 10rem;
        text-align: center;
        font-weight: 800;
        display: flex;
        justify-content: center;
        align-items: center;
        line-height: 272px;
    }

    .new-presentation :global(a:link) {
        box-sizing: border-box;
        border: 0.4rem dashed #a3a3a3;
        border-radius: 1rem;
        display: block;
        height: 100%;
        width: 100%;
        text-decoration: none;
        color: #a3a3a3;
    }

    .new-presentation :global(a:hover) {
        color: var(--active-foreground);
        border-color: var(--active-foreground);
    }

    @media (max-width: 575.98px) {
        .presentation-list {
            width: 100%;
            margin-left: 0;
            display: grid;
            list-style: none;
            padding: 0rem;
            padding-bottom: 0.5rem;
            padding-top: 0.5rem;
            grid-template-columns: repeat(auto-fit, minmax(99vw, 1fr));
            grid-auto-rows: 75vw;
            gap: 0rem;
            place-items: center;
        }

        .presentation-item {
            box-shadow: 0.3rem 0.3rem 1rem 0 rgba(0, 0, 0, 0.2);
            overflow: hidden;
            position: relative;
            width: 95vw;
            height: 70vw;
            border-radius: 1rem;
        }

        .presentation-title {
            color: #444444;
            display: block;
            position: absolute;
            width: 100%;
            bottom: 0;
            background-color: rgba(255, 255, 255, 0.95);
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            font-size: 1rem;
            font-weight: bold;
            padding: 0.75rem;
            box-sizing: border-box;
        }

        .new-presentation-placeholder {
            color: #a3a3a3;
            font-weight: bold;
            font-size: 1.5rem;
            box-sizing: border-box;
            border: 0.2rem dashed #a3a3a3;
            border-radius: 1rem;
            text-align: center;
            display: flex;
            justify-content: center;
            align-items: center;
        }

        .new-presentation {
            font-size: 10rem;
            text-align: center;
            font-weight: 500;
            display: flex;
            justify-content: center;
            align-items: center;
            line-height: 130px;
        }

        .new-presentation :global(a:link) {
            box-sizing: border-box;
            border: 0.2rem dashed #a3a3a3;
            border-radius: 1rem;
            display: block;
            height: 100%;
            width: 100%;
            text-decoration: none;
            color: #a3a3a3;
        }
    }
</style>
